import type { Edge, Node } from '@xyflow/react';
import type {
  AssigneeType,
  GateApproverType,
  GateMode,
  NodeExecutionDto,
  NodeKind,
  PortDirection,
  ArtifactKind,
  ProcessArtifactDto,
  ProcessGraphDto,
  ProcessNodeConfigDto,
  ProcessNodeDto,
  RunnerTarget,
  WriteProcessEdgeDto,
  WriteProcessNodeDto,
  WriteProcessPortDto,
} from '@api/processes';
import { LLM_MODEL_AUTO } from '@utils/llmModel';
import { stringList } from './taskSkillSelection';

// ── Port shape (mirrors plan §13.2) ──────────────────────────────────────────
// Ports are first-class entities with stable UUIDs. The editor uses the
// `portId` UUID as React Flow's Handle id so renaming a port (changing
// `label`) doesn't cascade through edge data — every edge stays wired to
// the same UUID.

export interface Port {
  portId: string;
  direction: PortDirection;
  label: string;
  /**
   * Canonical single artifact kind for the port. For multi-type input
   * ports (e.g. {@code image_gen}'s Prompt accepting both TEXT and
   * IMAGE) this is the primary kind used for the port chip's tint /
   * tooltip. The full multi-type set lives on {@link acceptedKinds}.
   */
  portType: ArtifactKind;
  /**
   * Full set of artifact kinds the port accepts (inputs) or emits
   * (outputs). For single-typed ports this is just {@code [portType]}.
   * Sourced from the backend catalog's {@code PortDefinitionDto.acceptedKinds};
   * absent when wire data was produced by an older backend — readers
   * should default to {@code [portType]}. Use {@link acceptedKindsOf}
   * for the safe access pattern.
   */
  acceptedKinds?: ArtifactKind[];
  position: number;
}

/**
 * Safe accessor for {@link Port.acceptedKinds} — falls back to a
 * singleton of {@code portType} when the field is absent. Centralised
 * so renderer code doesn't sprinkle the fallback inline.
 */
export function acceptedKindsOf(port: Port): readonly ArtifactKind[] {
  if (port.acceptedKinds && port.acceptedKinds.length > 0) {
    return port.acceptedKinds;
  }
  return [port.portType];
}

/** Mirrors the backend's `ArtifactKinds.anyRealKind()`. */
const ALL_REAL_ARTIFACT_KINDS: readonly ArtifactKind[] = [
  'MODEL_3D',
  'IMAGE',
  'POINT_CLOUD',
  'DOCUMENT',
  'GCODE',
  'TEXT',
  'NUMBER',
];

/** True when `kinds` is the catch-all set, in any order. */
export function acceptsAnyKind(kinds: readonly ArtifactKind[]): boolean {
  if (kinds.length !== ALL_REAL_ARTIFACT_KINDS.length) return false;
  const present = new Set(kinds);
  return ALL_REAL_ARTIFACT_KINDS.every((k) => present.has(k));
}

// ── Per-type NodeData shapes (discriminated by parent Node['type']) ─────────
// Each per-type shape carries:
//   • the wire-config fields flat (label, dataType, mode, ...) for ergonomic
//     reads from the renderer (no nested `config.dataType` lookups), and
//   • the `ports` array verbatim — the renderer iterates it to draw Handles.
// `reactFlowToDto` re-builds the wire-DTO `config` object from the relevant
// flat fields when saving back to the server.

/**
 * Persistent run snapshot carried through to the React Flow node data so a
 * page reload can restore "this node finished successfully last time, here
 * are the artifacts" without an additional fetch. Live state during an
 * active run is layered ON TOP via {@code useProcessRunSession.liveStateForNode}
 * (see {@link EditorContextValue}); this snapshot is the fallback rendered
 * when no live state exists.
 *
 * Both fields mirror the wire shape exactly (no derivation).
 *
 * {@code isRunnable} is the same flag the backend computes (a function of
 * node type + graph topology + validation). Carrying it into the React
 * Flow data lets the per-node "Run from here" button gate its
 * visibility without needing the renderer to reach back into the
 * detail cache.
 */
export interface NodeRunSnapshot {
  latestExecution: NodeExecutionDto | null;
  latestArtifacts: readonly ProcessArtifactDto[];
  isRunnable: boolean;
}

// {@code label} (= {@code process_nodes.label} on the wire) is the
// user-renameable, persisted node title. The editor's 3-dots quick menu
// Rename action writes here via
// {@code useProcessNodeActionHandlers#onRename}; the card title falls
// back to the catalog default ("Input", "Task", "Output", ...) when blank.
export interface InputNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  dataType: ArtifactKind;
  required: boolean;
  hint?: string;
  ports: Port[];
}

export interface TaskNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  /** In pick order, primary first; a blank selection reads as `['auto']`. */
  skillIds: string[];
  /** Catalog slug for the agent's ReAct loop; '' = none persisted, the
   *  skill's agent runs its own model. */
  llmModel: string;
  instruction: string;
  /** Per-output instruction keyed by portId. Stored in the config blob. */
  outputInstructions?: Record<string, string>;
  /** Per-execution credit cap for the agent; undefined = uncapped. */
  maxCredits?: number;
  /**
   * Where this node's agent runs. {@code 'CLOUD'} (the default) uses the
   * pooled process VM; {@code 'LOCAL'} runs on an assignee's own runner.
   * Always populated on the FE shape (coerce folds missing → 'CLOUD').
   */
  runnerTarget: RunnerTarget;
  /** LOCAL assignee mode (USER = assigneeIds, GROUP = assigneeGroupId). */
  assigneeType: AssigneeType;
  /** Named assignees when {@link assigneeType} is USER. */
  assigneeIds: string[];
  /** Selected org group id when {@link assigneeType} is GROUP. */
  assigneeGroupId?: string;
  /** LOCAL wait bound in hours before the node fails; undefined = default. */
  timeoutHours?: number;
  ports: Port[];
}

export interface GateNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  mode: GateMode;
  /** MANUAL approver mode (USER = reviewerIds, GROUP = reviewerGroupId). */
  approverType: GateApproverType;
  reviewerIds: string[];
  /** Selected org group id when {@link approverType} is GROUP. */
  reviewerGroupId?: string;
  /**
   * AUTO-mode reviewer skill id. Required when {@link mode} is
   * {@code 'AUTO'}; ignored otherwise. Optional on the FE shape so
   * MANUAL gates round-trip without carrying a stale skill id.
   */
  reviewerSkillId?: string;
  /**
   * AUTO-mode reviewer model (OpenRouter slug from the shared LLM
   * catalog), or the {@link LLM_MODEL_AUTO} sentinel ('auto') meaning
   * "let the reviewer agent use its own default chain". Always
   * populated on the FE shape (coerce folds missing/blank → 'auto')
   * so the SELECT control renders "Auto" without a null-check. Only
   * persisted for AUTO gates and only when not 'auto' (see
   * {@link buildGateConfig}); MANUAL gates and Auto picks carry
   * nothing on the wire, and the backend {@code GateConfig} folds any
   * non-catalogued value back to {@code null}.
   */
  llmModel: string;
  /**
   * AUTO-mode iteration cap (1..10, default 5). Drives the SELECT
   * control on the gate detail panel and the "Iteration: x/N"
   * subtitle on the running card. Mirrors backend
   * {@code GateConfig#maxIterations}; the React-Flow wire-side
   * coercion ({@link coerceNodeData}) folds missing values back to
   * the default so legacy rows light up with the same denominator
   * the runtime guard uses. Always populated on the FE shape (the
   * backend's {@code @JsonCreator} normalises the wire format) so
   * consumers can read it without a null-check.
   */
  maxIterations: number;
  reviewInstructions: string;
  timeoutHours: number;
  inputType: ArtifactKind;
  ports: Port[];
}

export interface OutputNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  dataType: ArtifactKind;
  ports: Port[];
}

export interface ImageGenNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  instruction: string;
  ports: Port[];
  heroSize?: { width: number; height: number };
  /** Natural slot size captured on the first drag — the lower bound for subsequent drags. */
  heroMinSize?: { width: number; height: number };
  /** Selected fal.ai endpoint for text-to-image; blank = service default. */
  genModel?: string;
  /** Selected fal.ai endpoint for image editing; blank = service default. */
  editModel?: string;
}

export interface ThreeDGenNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  instruction: string;
  ports: Port[];
}

export interface NoteNodeData extends Record<string, unknown> {
  text: string;
}

export interface ManualNodeData extends Record<string, unknown>, NodeRunSnapshot {
  label: string;
  /** Assignee mode (USER = assigneeIds, GROUP = assigneeGroupId). */
  assigneeType: AssigneeType;
  /** Named assignees when {@link assigneeType} is USER. */
  assigneeIds: string[];
  /** Selected org group id when {@link assigneeType} is GROUP. */
  assigneeGroupId?: string;
  /** Free-text instructions shown to the assignee. */
  instructions?: string;
  ports: Port[];
}

export type ProcessNodeData =
  | InputNodeData
  | TaskNodeData
  | GateNodeData
  | OutputNodeData
  | ImageGenNodeData
  | ThreeDGenNodeData
  | ManualNodeData
  | NoteNodeData;

// ── Branch-edge accent (visual-only, derived from source port label) ────────

export type EdgeAccent = 'default' | 'approved' | 'rejected';

export interface ProcessEdgeData extends Record<string, unknown> {
  accent: EdgeAccent;
}

// ── React Flow types specialised for our editor ─────────────────────────────

export type ProcessNode = Node<ProcessNodeData, NodeKind>;
export type ProcessEdge = Edge<ProcessEdgeData>;

// ── Validation helpers for incoming wire data ───────────────────────────────

const VALID_NODE_KINDS = new Set<NodeKind>([
  'input',
  'task',
  'gate',
  'output',
  'note',
  'image_gen',
  '3d_gen',
  'manual',
]);

const VALID_ARTIFACT_KINDS = new Set<ArtifactKind>([
  'MODEL_3D',
  'IMAGE',
  'POINT_CLOUD',
  'DOCUMENT',
  'GCODE',
  'TEXT',
  'NUMBER',
  'NONE',
]);

const VALID_DIRECTIONS = new Set<PortDirection>(['input', 'output']);

const VALID_GATE_MODES = new Set<GateMode>(['MANUAL', 'AUTO']);

const isArtifactKind = (v: unknown): v is ArtifactKind =>
  typeof v === 'string' && VALID_ARTIFACT_KINDS.has(v as ArtifactKind);

const isNodeKind = (v: unknown): v is NodeKind =>
  typeof v === 'string' && VALID_NODE_KINDS.has(v as NodeKind);

const isPortDirection = (v: unknown): v is PortDirection =>
  typeof v === 'string' && VALID_DIRECTIONS.has(v as PortDirection);

const isGateMode = (v: unknown): v is GateMode =>
  typeof v === 'string' && VALID_GATE_MODES.has(v as GateMode);

const safeKind = (v: unknown): NodeKind => (isNodeKind(v) ? v : 'note');

// ── ID factory ──────────────────────────────────────────────────────────────
// `crypto.randomUUID` is available in modern Node + browsers + jsdom. The
// fallback exists so a missing crypto API in older environments still
// produces a UUID-shaped string rather than crashing the editor.

export function makeId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // RFC4122 v4 fallback. Only hit in non-secure contexts (e.g. a stripped
  // jsdom build) — production always has crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── Port coercion (defensive — wire data may be partial) ────────────────────

const coerceAcceptedKinds = (
  raw: unknown,
  fallback: ArtifactKind,
): ArtifactKind[] => {
  if (!Array.isArray(raw)) return [fallback];
  const filtered = raw.filter(isArtifactKind);
  return filtered.length > 0 ? filtered : [fallback];
};

const coercePort = (raw: unknown, fallbackPosition: number): Port => {
  const obj = (raw !== null && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  const portId =
    typeof obj.portId === 'string' && obj.portId.length > 0
      ? obj.portId
      : makeId();
  const direction = isPortDirection(obj.direction) ? obj.direction : 'output';
  const label = typeof obj.label === 'string' ? obj.label : 'Port';
  const portType = isArtifactKind(obj.portType) ? obj.portType : 'MODEL_3D';
  const acceptedKinds = coerceAcceptedKinds(obj.acceptedKinds, portType);
  const position =
    typeof obj.position === 'number' ? obj.position : fallbackPosition;
  return { portId, direction, label, portType, acceptedKinds, position };
};

const coercePorts = (raw: unknown): Port[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((p, i) => coercePort(p, i));
};

// ── Coerce a server-side ProcessNodeDto into the per-type editor data ───────

interface CoerceContext {
  readonly config: ProcessNodeConfigDto;
  readonly ports: Port[];
  readonly label: string;
  readonly definitionId: string;
  readonly snapshot: NodeRunSnapshot;
  readonly safe: Partial<
    Pick<
      ProcessNodeDto,
      | 'label'
      | 'definitionId'
      | 'instruction'
      | 'config'
      | 'ports'
      | 'latestExecution'
      | 'latestArtifacts'
    >
  >;
}

const coerceInputData = (ctx: CoerceContext): InputNodeData => {
  const dataType = isArtifactKind(ctx.config.dataType) ? ctx.config.dataType : 'NONE';
  return {
    label: ctx.label || 'Input',
    dataType,
    required:
      typeof ctx.config.required === 'boolean' ? ctx.config.required : false,
    hint: typeof ctx.config.text === 'string' ? ctx.config.text : undefined,
    ports: ctx.ports,
    ...ctx.snapshot,
  };
};

/**
 * Mirrors the Java {@code TaskSkillCatalog.AUTO_SKILL_ID}. The backend serves it
 * as the first entry of both Task skill dropdowns and resolves it to a concrete
 * skill per run, so the editor treats it as an ordinary selectable value.
 */
export const TASK_AUTO_SKILL_ID = 'auto';

/** "task" is the structural definition id, never a skill, so it reads as "no
 *  skill picked" wherever a persisted blob holds it. */
const cleanTaskSkillId = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 && trimmed !== 'task' ? trimmed : undefined;
};

const coercedTaskSkillIds = (ctx: CoerceContext): string[] => {
  const raw = ctx.config.skillIds;
  if (Array.isArray(raw)) {
    const ids = [...new Set(raw.map(cleanTaskSkillId).filter((id) => id !== undefined))];
    if (ids.length > 0) return ids;
  }
  const single =
    cleanTaskSkillId(ctx.config.skillId) ?? cleanTaskSkillId(ctx.config.agentId);
  if (single) return [single];
  if (ctx.definitionId && ctx.definitionId !== 'task') {
    return [ctx.definitionId];
  }
  return [TASK_AUTO_SKILL_ID];
};

// Mirrors the Java {@code TaskLlmModelCatalog.defaultModel()}. Seeds a
// freshly dropped Task only (see paletteDrop); a persisted row that carries
// no model is left without one, so a legacy process is never re-priced by
// the editor filling in the current default behind the user's back.
export const TASK_DEFAULT_LLM_MODEL = 'openai/gpt-6.1-sol';

const coerceTaskLlmModel = (raw: unknown): string =>
  typeof raw === 'string' ? raw.trim() : '';

/**
 * Parse a persisted numeric field that may arrive as a number, as a
 * string (hand-edited row or SELECT control), or be missing entirely;
 * anything non-numeric yields {@code NaN} for the caller to range-check.
 */
const toNumericOrNaN = (raw: unknown): number => {
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') return Number(raw);
  return Number.NaN;
};

/**
 * Coerce the persisted {@code maxCredits} value (a number from the
 * JSON, or a string if the row was hand-edited) into a positive finite
 * number; anything else means uncapped.
 */
const coerceTaskMaxCredits = (raw: unknown): number | undefined => {
  const parsed = toNumericOrNaN(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const coerceRunnerTarget = (raw: unknown): RunnerTarget =>
  raw === 'LOCAL' ? 'LOCAL' : 'CLOUD';

const coerceAssigneeType = (raw: unknown): AssigneeType =>
  raw === 'GROUP' ? 'GROUP' : 'USER';

const coerceStringList = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];

const coerceTaskTimeoutHours = (raw: unknown): number | undefined => {
  const parsed = toNumericOrNaN(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : undefined;
};

const coerceTaskData = (ctx: CoerceContext): TaskNodeData => {
  const maxCredits = coerceTaskMaxCredits(ctx.config.maxCredits);
  const assigneeGroupId =
    typeof ctx.config.assigneeGroupId === 'string' && ctx.config.assigneeGroupId.length > 0
      ? ctx.config.assigneeGroupId
      : undefined;
  const timeoutHours = coerceTaskTimeoutHours(ctx.config.timeoutHours);
  return {
    label: ctx.label || 'Task',
    skillIds: coercedTaskSkillIds(ctx),
    llmModel: coerceTaskLlmModel(ctx.config.llmModel),
    instruction:
      typeof ctx.safe.instruction === 'string' ? ctx.safe.instruction : '',
    outputInstructions:
      ctx.config.outputInstructions &&
      typeof ctx.config.outputInstructions === 'object'
        ? ctx.config.outputInstructions
        : undefined,
    ...(maxCredits === undefined ? {} : { maxCredits }),
    runnerTarget: coerceRunnerTarget(ctx.config.runnerTarget),
    assigneeType: coerceAssigneeType(ctx.config.assigneeType),
    assigneeIds: coerceStringList(ctx.config.assigneeIds),
    ...(assigneeGroupId ? { assigneeGroupId } : {}),
    ...(timeoutHours === undefined ? {} : { timeoutHours }),
    ports: ctx.ports,
    ...ctx.snapshot,
  };
};

// Mirrors the Java {@code GateConfig.DEFAULT_AUTO_ITERATIONS}. Kept
// inline here (rather than imported from a shared module) so the
// coerce helper has no extra dependencies; if either side moves, the
// {@code resolveGateRunSubtitle} unit tests pin the same default.
const GATE_DEFAULT_MAX_ITERATIONS = 5;
const GATE_MIN_AUTO_ITERATIONS = 1;
const GATE_MAX_AUTO_ITERATIONS = 10;

/**
 * Coerce the persisted {@code maxIterations} value (which may arrive
 * as a number from the JSON, as a string from the SELECT control, or
 * be missing entirely on legacy rows) into the supported {@code [1..10]}
 * range. Falls back to {@link GATE_DEFAULT_MAX_ITERATIONS} for any
 * unparseable / absent value so the FE shape is always populated.
 */
const coerceGateMaxIterations = (raw: unknown): number => {
  const parsed = toNumericOrNaN(raw);
  if (!Number.isFinite(parsed)) return GATE_DEFAULT_MAX_ITERATIONS;
  if (parsed < GATE_MIN_AUTO_ITERATIONS) return GATE_MIN_AUTO_ITERATIONS;
  if (parsed > GATE_MAX_AUTO_ITERATIONS) return GATE_MAX_AUTO_ITERATIONS;
  return Math.floor(parsed);
};

const coerceGateData = (ctx: CoerceContext): GateNodeData => {
  const inputType = isArtifactKind(ctx.config.inputType)
    ? ctx.config.inputType
    : 'MODEL_3D';
  const rawIds = ctx.config.reviewerIds;
  const reviewerIds = Array.isArray(rawIds)
    ? rawIds.filter((id): id is string => typeof id === 'string')
    : [];
  // Rows saved before the skillId rename carry the legacy reviewerAgentId key.
  const rawReviewerSkill =
    typeof ctx.config.reviewerSkillId === 'string'
      ? ctx.config.reviewerSkillId
      : ctx.config.reviewerAgentId;
  const reviewerSkillId =
    typeof rawReviewerSkill === 'string' && rawReviewerSkill.length > 0
      ? rawReviewerSkill
      : undefined;
  const approverType: GateApproverType =
    ctx.config.approverType === 'GROUP' ? 'GROUP' : 'USER';
  const rawGroupId = ctx.config.reviewerGroupId;
  const reviewerGroupId =
    typeof rawGroupId === 'string' && rawGroupId.length > 0 ? rawGroupId : undefined;
  // Legacy rows (and MANUAL gates) carry no llmModel key; fold to the
  // 'auto' sentinel so the SELECT always renders "Auto" preselected.
  const rawModel = ctx.config.llmModel;
  const llmModel =
    typeof rawModel === 'string' && rawModel.trim().length > 0
      ? rawModel.trim()
      : LLM_MODEL_AUTO;
  return {
    label: ctx.label || 'Review',
    mode: isGateMode(ctx.config.mode) ? ctx.config.mode : 'MANUAL',
    approverType,
    reviewerIds,
    ...(reviewerGroupId ? { reviewerGroupId } : {}),
    ...(reviewerSkillId ? { reviewerSkillId } : {}),
    llmModel,
    maxIterations: coerceGateMaxIterations(ctx.config.maxIterations),
    reviewInstructions:
      typeof ctx.config.reviewInstructions === 'string'
        ? ctx.config.reviewInstructions
        : '',
    timeoutHours:
      typeof ctx.config.timeoutHours === 'number' ? ctx.config.timeoutHours : 24,
    inputType,
    ports: ctx.ports,
    ...ctx.snapshot,
  };
};

const coerceOutputData = (ctx: CoerceContext): OutputNodeData => {
  const dataType = isArtifactKind(ctx.config.dataType) ? ctx.config.dataType : 'MODEL_3D';
  return {
    label: ctx.label || 'Output',
    dataType,
    ports: ctx.ports,
    ...ctx.snapshot,
  };
};

const coerceHeroSize = (
  raw: unknown,
): { width: number; height: number } | undefined => {
  if (raw === null || typeof raw !== 'object') return undefined;
  const { width, height } = raw as { width?: unknown; height?: unknown };
  // Reject NaN / Infinity / zero / negatives — heroSize is rendered as
  // an inline CSS dimension and any of those would break layout.
  if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) return undefined;
  if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) return undefined;
  return { width, height };
};

const coerceModelId = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.trim().length > 0 ? raw : undefined;

const coerceImageGenData = (ctx: CoerceContext): ImageGenNodeData => {
  const heroSize = coerceHeroSize(ctx.config.heroSize);
  const heroMinSize = coerceHeroSize(ctx.config.heroMinSize);
  const genModel = coerceModelId(ctx.config.genModel);
  const editModel = coerceModelId(ctx.config.editModel);
  return {
    label: ctx.label || 'Image Generation',
    instruction: typeof ctx.safe.instruction === 'string' ? ctx.safe.instruction : '',
    ports: ctx.ports,
    ...(heroSize ? { heroSize } : {}),
    ...(heroMinSize ? { heroMinSize } : {}),
    ...(genModel ? { genModel } : {}),
    ...(editModel ? { editModel } : {}),
    ...ctx.snapshot,
  };
};

const coerceThreeDGenData = (ctx: CoerceContext): ThreeDGenNodeData => ({
  label: ctx.label || '3D Generation',
  instruction: typeof ctx.safe.instruction === 'string' ? ctx.safe.instruction : '',
  ports: ctx.ports,
  ...ctx.snapshot,
});

const coerceManualData = (ctx: CoerceContext): ManualNodeData => {
  const assigneeGroupId =
    typeof ctx.config.assigneeGroupId === 'string' && ctx.config.assigneeGroupId.length > 0
      ? ctx.config.assigneeGroupId
      : undefined;
  const instructions =
    typeof ctx.config.instructions === 'string' && ctx.config.instructions.length > 0
      ? ctx.config.instructions
      : undefined;
  return {
    label: ctx.label || 'Manual',
    assigneeType: coerceAssigneeType(ctx.config.assigneeType),
    assigneeIds: coerceStringList(ctx.config.assigneeIds),
    ...(assigneeGroupId ? { assigneeGroupId } : {}),
    ...(instructions ? { instructions } : {}),
    ports: ctx.ports,
    ...ctx.snapshot,
  };
};

const coerceNoteData = (ctx: CoerceContext): NoteNodeData => ({
  text: typeof ctx.config.text === 'string' ? ctx.config.text : ctx.label,
});

const coerceRunSnapshot = (
  raw: Partial<Pick<ProcessNodeDto, 'latestExecution' | 'latestArtifacts'>>,
  kind: NodeKind,
): NodeRunSnapshot => ({
  // The wire DTO either omits these (defensively shaped fixtures, write-side
  // round-trips) or includes them. Treat undefined as null / empty so the
  // node renderer can rely on a stable shape.
  latestExecution: raw.latestExecution ?? null,
  latestArtifacts: Array.isArray(raw.latestArtifacts) ? raw.latestArtifacts : [],
  // Note is the only non-computable kind today; every other kind is
  // runnable from the engine's perspective. Mirrors the backend's
  // `NodeDefinition.isComputable()` default.
  isRunnable: kind !== 'note',
});

export function coerceNodeData(
  kind: NodeKind,
  raw: ProcessNodeDto | null | undefined,
): ProcessNodeData {
  const safe = (raw !== null && typeof raw === 'object' ? raw : {}) as Partial<
    Pick<
      ProcessNodeDto,
      | 'label'
      | 'definitionId'
      | 'instruction'
      | 'config'
      | 'ports'
      | 'latestExecution'
      | 'latestArtifacts'
    >
  >;
  const ctx: CoerceContext = {
    config: safe.config ?? {},
    ports: coercePorts(safe.ports),
    label: typeof safe.label === 'string' ? safe.label : '',
    definitionId: typeof safe.definitionId === 'string' ? safe.definitionId : '',
    snapshot: coerceRunSnapshot(safe, kind),
    safe,
  };

  switch (kind) {
    case 'input':
      return coerceInputData(ctx);
    case 'task':
      return coerceTaskData(ctx);
    case 'gate':
      return coerceGateData(ctx);
    case 'output':
      return coerceOutputData(ctx);
    case 'image_gen':
      return coerceImageGenData(ctx);
    case '3d_gen':
      return coerceThreeDGenData(ctx);
    case 'manual':
      return coerceManualData(ctx);
    case 'note':
      return coerceNoteData(ctx);
    default:
      return { text: ctx.label };
  }
}

// ── Branch-edge accent resolution ───────────────────────────────────────────

export function resolveAccent(
  nodes: readonly ProcessNode[],
  sourceNodeId: string | undefined | null,
  sourcePortId: string | undefined | null,
): EdgeAccent {
  if (!sourceNodeId || !sourcePortId) return 'default';
  const sourceNode = nodes.find((n) => n.id === sourceNodeId);
  if (sourceNode?.type !== 'gate') return 'default';
  const data = sourceNode.data as GateNodeData;
  const port = data.ports.find((p) => p.portId === sourcePortId);
  if (!port) return 'default';
  const lower = port.label.toLowerCase();
  if (lower === 'approved') return 'approved';
  if (lower === 'rejected') return 'rejected';
  return 'default';
}

// ── Conversions between wire DTO and React Flow shapes ──────────────────────

export function dtoToReactFlow(graph: ProcessGraphDto): {
  nodes: ProcessNode[];
  edges: ProcessEdge[];
} {
  const nodes: ProcessNode[] = graph.nodes.map((n) => {
    const kind = safeKind(n.kind);
    return {
      id: n.nodeId,
      type: kind,
      position: { x: n.positionX, y: n.positionY },
      data: coerceNodeData(kind, n),
      // The whole node card is its own visual surface — React Flow's drag
      // listener uses the header's `node-card__drag-handle` class.
      dragHandle: '.node-card__drag-handle',
    };
  });
  const edges: ProcessEdge[] = graph.edges.map((e) => ({
    id: e.edgeId,
    source: e.sourceNodeId,
    sourceHandle: e.sourcePortId,
    target: e.targetNodeId,
    targetHandle: e.targetPortId,
    type: 'branched',
    data: { accent: resolveAccent(nodes, e.sourceNodeId, e.sourcePortId) },
  }));
  return { nodes, edges };
}

// ── React Flow → wire DTO ───────────────────────────────────────────────────

const buildInputConfig = (d: InputNodeData): ProcessNodeConfigDto => ({
  dataType: d.dataType,
  required: d.required,
  ...(d.hint ? { text: d.hint } : {}),
});

// Defensive: GateNodeData declares `reviewerIds: string[]` but the
// backend's GateConfig.defaults() ships null (the "anyone can review"
// sentinel) and that null can leak through the catalog-driven palette
// drop. Coerce here so `.length` is safe even if a future path forgets
// to normalise upstream.
const buildGateConfig = (d: GateNodeData): ProcessNodeConfigDto => {
  const reviewerIds = Array.isArray(d.reviewerIds) ? d.reviewerIds : [];
  const mode: GateMode = d.mode === 'AUTO' ? 'AUTO' : 'MANUAL';
  // Only ship `reviewerSkillId` for AUTO gates so MANUAL rows don't
  // carry a stale id from a previous toggle. The backend's
  // {@code GateNodesConfiguredRule} fails an AUTO gate without an
  // agent, which surfaces in the editor through the validation
  // hint -- the FE doesn't try to second-guess that here.
  const reviewerSkillId =
    mode === 'AUTO' && typeof d.reviewerSkillId === 'string' && d.reviewerSkillId.length > 0
      ? d.reviewerSkillId
      : undefined;
  // Like the chat picker, only ship a concrete model slug: 'auto' (the
  // default) and MANUAL gates persist nothing, so the reviewer agent
  // keeps its own default chain. The backend also folds any
  // non-catalogued value back to null, so this is belt-and-braces.
  const llmModel =
    mode === 'AUTO' &&
    typeof d.llmModel === 'string' &&
    d.llmModel.length > 0 &&
    d.llmModel !== LLM_MODEL_AUTO
      ? d.llmModel
      : undefined;
  // Mirror the backend's mutual-exclusion: a GROUP approver ships the group
  // id and drops reviewerIds; USER (or AUTO) ships the people list only.
  const approverType: GateApproverType = d.approverType === 'GROUP' ? 'GROUP' : 'USER';
  const reviewerGroupId =
    approverType === 'GROUP' && typeof d.reviewerGroupId === 'string' && d.reviewerGroupId.length > 0
      ? d.reviewerGroupId
      : undefined;
  return {
    mode,
    approverType,
    reviewerIds:
      approverType === 'USER' && reviewerIds.length > 0 ? reviewerIds : undefined,
    ...(reviewerGroupId ? { reviewerGroupId } : {}),
    reviewInstructions:
      typeof d.reviewInstructions === 'string' ? d.reviewInstructions : '',
    timeoutHours:
      typeof d.timeoutHours === 'number' ? d.timeoutHours : 24,
    inputType: d.inputType,
    ...(reviewerSkillId ? { reviewerSkillId } : {}),
    ...(llmModel ? { llmModel } : {}),
    // {@code maxIterations} is canonical on the FE shape (the
    // coerce helper ensures it is always set), so we always ship it
    // on save -- no `if (mode === 'AUTO')` guard. The backend
    // ignores the field for MANUAL gates anyway, and persisting it
    // for both modes lets a MANUAL→AUTO toggle keep the user's
    // last cap choice instead of resetting to the default.
    maxIterations: coerceGateMaxIterations(d.maxIterations),
  };
};

const buildTaskConfig = (d: TaskNodeData): ProcessNodeConfigDto => {
  // The NUMBER control writes null on clear; ship the key only for a
  // positive value so a cleared field round-trips back to uncapped.
  const maxCredits = coerceTaskMaxCredits(d.maxCredits);
  const runnerTarget: RunnerTarget = d.runnerTarget === 'LOCAL' ? 'LOCAL' : 'CLOUD';
  // Assignee routing only applies to LOCAL nodes. Mirror the gate's
  // mutual-exclusion: a GROUP assignee ships the group id and drops the
  // people list; USER ships the people list only. CLOUD drops all of it
  // so a LOCAL→CLOUD toggle doesn't leave stale routing behind.
  const local = buildLocalTaskRouting(runnerTarget, d);
  const skillIds = stringList(d.skillIds).filter((id) => id.length > 0);
  const llmModel = coerceTaskLlmModel(d.llmModel);
  return {
    ...(skillIds.length > 0 ? { skillIds, skillId: skillIds[0] } : {}),
    // Empty means the row never had a model; ship no key so the backend
    // keeps it null (agent default) rather than pinning one.
    ...(llmModel ? { llmModel } : {}),
    ...(d.outputInstructions && Object.keys(d.outputInstructions).length > 0
      ? { outputInstructions: d.outputInstructions }
      : {}),
    ...(maxCredits === undefined ? {} : { maxCredits }),
    runnerTarget,
    ...local,
  };
};

const buildLocalTaskRouting = (
  runnerTarget: RunnerTarget,
  d: TaskNodeData,
): Partial<ProcessNodeConfigDto> => {
  if (runnerTarget !== 'LOCAL') return {};
  const assigneeType: AssigneeType = d.assigneeType === 'GROUP' ? 'GROUP' : 'USER';
  const assigneeIds = Array.isArray(d.assigneeIds) ? d.assigneeIds : [];
  const assigneeGroupId =
    assigneeType === 'GROUP' &&
    typeof d.assigneeGroupId === 'string' &&
    d.assigneeGroupId.length > 0
      ? d.assigneeGroupId
      : undefined;
  const timeoutHours = coerceTaskTimeoutHours(d.timeoutHours);
  return {
    assigneeType,
    ...(assigneeType === 'USER' && assigneeIds.length > 0 ? { assigneeIds } : {}),
    ...(assigneeGroupId ? { assigneeGroupId } : {}),
    ...(timeoutHours === undefined ? {} : { timeoutHours }),
  };
};

const buildManualConfig = (d: ManualNodeData): ProcessNodeConfigDto => {
  const assigneeType: AssigneeType = d.assigneeType === 'GROUP' ? 'GROUP' : 'USER';
  const assigneeIds = Array.isArray(d.assigneeIds) ? d.assigneeIds : [];
  const assigneeGroupId =
    assigneeType === 'GROUP' &&
    typeof d.assigneeGroupId === 'string' &&
    d.assigneeGroupId.length > 0
      ? d.assigneeGroupId
      : undefined;
  const instructions =
    typeof d.instructions === 'string' && d.instructions.length > 0
      ? d.instructions
      : undefined;
  return {
    assigneeType,
    ...(assigneeType === 'USER' && assigneeIds.length > 0 ? { assigneeIds } : {}),
    ...(assigneeGroupId ? { assigneeGroupId } : {}),
    ...(instructions ? { instructions } : {}),
  };
};

const buildImageGenConfig = (d: ImageGenNodeData): ProcessNodeConfigDto => {
  const cfg: Record<string, unknown> = {};
  if (d.heroSize) cfg.heroSize = d.heroSize;
  if (d.heroMinSize) cfg.heroMinSize = d.heroMinSize;
  if (d.genModel) cfg.genModel = d.genModel;
  if (d.editModel) cfg.editModel = d.editModel;
  return cfg;
};

const buildConfig = (
  kind: NodeKind,
  data: ProcessNodeData,
): ProcessNodeConfigDto => {
  switch (kind) {
    case 'input':
      return buildInputConfig(data as InputNodeData);
    case 'output':
      return { dataType: (data as OutputNodeData).dataType };
    case 'gate':
      return buildGateConfig(data as GateNodeData);
    case 'note':
      return { text: (data as NoteNodeData).text };
    case 'task':
      return buildTaskConfig(data as TaskNodeData);
    case 'manual':
      return buildManualConfig(data as ManualNodeData);
    case 'image_gen':
      return buildImageGenConfig(data as ImageGenNodeData);
    default:
      return {};
  }
};

/**
 * Project the runtime React Flow port shape onto the wire-side
 * {@link WriteProcessPortDto}. Strips {@code acceptedKinds} on purpose:
 * the backend is now the single source of truth for that field (and for
 * {@code portType} on bound ports). The FE round-trip just sends the
 * structural shape; the BE re-derives port_type / accepted_kinds_json
 * from the catalog and the wire config on every save.
 */
const portsToWire = (
  kind: NodeKind,
  data: ProcessNodeData,
): WriteProcessPortDto[] => {
  if (kind === 'note') return [];
  const ports = (data as { ports?: Port[] }).ports;
  if (!Array.isArray(ports)) return [];
  return ports.map((p) => ({
    portId: p.portId,
    direction: p.direction,
    label: p.label,
    portType: p.portType,
    position: p.position,
  }));
};

/**
 * Resolve the wire-side {@code label} for a node. The DB column is
 * {@code NOT NULL} (and the read-side coercion uses {@code label} as a
 * display fallback) so we always send something meaningful.
 *
 * Note nodes don't carry a user-visible label -- the editor stores their
 * content under {@code data.text} and the backend echoes it via
 * {@code config.text}. Sending {@code label: ''} would either trip a
 * future {@code @NotBlank} or leave the row with a confusing blank. We
 * default to {@code 'Note'} so anyone querying the {@code process_nodes}
 * table directly sees a sensible row name.
 */
const deriveLabel = (kind: NodeKind, data: ProcessNodeData): string => {
  if (kind === 'note') return 'Note';
  const raw = (data as { label?: unknown }).label;
  return typeof raw === 'string' ? raw : '';
};

/**
 * Resolve the wire-side {@code definitionId} for a node. All tasks now
 * share the single {@code "task"} definition; the skills are selected via
 * the config-blob {@code skillIds} field. Other kinds map 1:1.
 */
const deriveDefinitionId = (kind: NodeKind): string => {
  if (kind === 'task') return 'task';
  if (kind === 'image_gen') return 'image_gen';
  if (kind === '3d_gen') return '3d_gen';
  return kind;
};

/**
 * Serialise React Flow state into the auto-save body shape
 * ({@link WriteProcessNodeDto} / {@link WriteProcessEdgeDto}).
 *
 * Read-side display values like {@code latestExecution} and
 * {@code latestArtifacts} are dropped -- they aren't user-editable.
 * {@code kind} is dropped too -- the server reads it off the resolved
 * {@code definitionId / definitionVersion} pair.
 */
export function reactFlowToDto(
  nodes: readonly ProcessNode[],
  edges: readonly ProcessEdge[],
): { nodes: WriteProcessNodeDto[]; edges: WriteProcessEdgeDto[] } {
  const nodeDtos: WriteProcessNodeDto[] = nodes.map((n) => {
    const kind = safeKind(n.type);
    const data = n.data;
    const hasInstruction = kind === 'task' || kind === 'image_gen' || kind === '3d_gen';
    const withInstruction = data as { instruction?: string };
    return {
      nodeId: n.id,
      definitionId: deriveDefinitionId(kind),
      definitionVersion: 1,
      label: deriveLabel(kind, data),
      instruction: hasInstruction ? (withInstruction.instruction ?? '') : null,
      config: buildConfig(kind, data),
      ports: portsToWire(kind, data),
      positionX: n.position.x,
      positionY: n.position.y,
    };
  });
  // Safety net: an edge wired to a port that no longer exists on its node
  // (stale after a port removal, a concurrent-tab merge, etc.) makes the
  // backend reject the whole save with "Edge references unknown node or
  // port". Drop those edges here so a serialized body can never carry one.
  const portsByNode = new Map<string, Set<string>>(
    nodes.map((n) => [
      n.id,
      new Set(((n.data as { ports?: Port[] }).ports ?? []).map((p) => p.portId)),
    ]),
  );
  const edgeDtos: WriteProcessEdgeDto[] = edges
    .filter(
      (e) =>
        !!e.sourceHandle &&
        !!e.targetHandle &&
        !!portsByNode.get(e.source)?.has(e.sourceHandle) &&
        !!portsByNode.get(e.target)?.has(e.targetHandle),
    )
    .map((e) => ({
      edgeId: e.id,
      sourceNodeId: e.source,
      sourcePortId: e.sourceHandle ?? '',
      targetNodeId: e.target,
      targetPortId: e.targetHandle ?? '',
    }));
  return { nodes: nodeDtos, edges: edgeDtos };
}
